import { GridOptions, ColGroupDef, ColDef } from 'ag-grid-community'
import { FatFingerUserLimitDetail } from 'api'
import { createActionsRenderer, gridOptionsBase, TypedValueGetterParams, withAllContext } from 'view/components/datagrid'
import { Translation, Locale } from 'locale'
import { FatFingerUserLimitActionsButton } from './FatFingerLimitsActionsButton'
import { makeNumberFilterGetter, makeNumberFormatter, makeNumberQuickFilterTextGetter } from 'view/shared/datagrid'

export enum TradingFatFingerGridColumnId {
  FAT_FINGER_CHECKBOX = 'FAT_FINGER_CHECKBOX',
  FAT_FINGER_ACTIONS = 'FAT_FINGER_ACTIONS',
  FAT_FINGER_SECTOR = 'FAT_FINGER_SECTOR',
  FAT_FINGER_PRODUCT = 'FAT_FINGER_PRODUCT',
  FAT_FINGER_FIXED_RATE_PRICE = 'FAT_FINGER_FIXED_RATE_PRICE',
  FAT_FINGER_FLOAT_RATE_SPREAD = 'FAT_FINGER_FLOAT_RATE_SPREAD',
  FAT_FINGER_FIXED_RATE_RESTRICTION = 'FAT_FINGER_FIXED_RATE_RESTRICTION',
  FAT_FINGER_FIXED_RATE_WARN_LEVEL = 'FAT_FINGER_FIXED_RATE_WARN_LEVEL',
  FAT_FINGER_FLOAT_RATE_WARN_LEVEL = 'FAT_FINGER_FLOAT_RATE_WARN_LEVEL',
  FAT_FINGER_FLOAT_RATE_RESTRICTION = 'FAT_FINGER_FLOAT_RATE_RESTRICTION',
  FAT_FINGER_NOTIONAL_AMOUNT_WARN = 'FAT_FINGER_NOTIONAL_AMOUNT_WARN',
  FAT_FINGER_NOTIONAL_AMOUNT_RESTRICTION = 'FAT_FINGER_NOTIONAL_AMOUNT_RESTRICTION',
  FAT_FINGER_CASH_AMOUNT_WARN = 'FAT_FINGER_CASH_AMOUNT_WARN',
  FAT_FINGER_CASH_AMOUNT_RESTRICTION = 'FAT_FINGER_CASH_AMOUNT_RESTRICTION'
}

export const tradingFatFingerLimitsColumnDefs = (): (ColGroupDef | ColDef)[] => {
  return [
    {
      colId: TradingFatFingerGridColumnId.FAT_FINGER_ACTIONS,
      headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
      equals: () => false,
      width: 150,
      cellClass: 'action-cell',
      cellRenderer: withAllContext(createActionsRenderer(FatFingerUserLimitActionsButton)),
      pinned: 'left',
      lockPinned: true,
      enableRowGroup: false,
      lockVisible: true,
      filter: false,
      sortable: false
    },
    {
      colId: TradingFatFingerGridColumnId.FAT_FINGER_SECTOR,
      valueGetter: (params: TypedValueGetterParams<FatFingerUserLimitDetail>) => params?.data?.productSector?.name,
      headerName: Locale.translate(Translation.TC_SECTOR),
      width: 120,
      filter: 'agSetColumnFilter'
    },
    {
      colId: TradingFatFingerGridColumnId.FAT_FINGER_PRODUCT,
      valueGetter: (params: TypedValueGetterParams<FatFingerUserLimitDetail>) => params?.data?.product?.name,
      headerName: Locale.translate(Translation.TC_PRODUCT),
      width: 120,
      filter: 'agSetColumnFilter'
    },
    {
      colId: TradingFatFingerGridColumnId.FAT_FINGER_FIXED_RATE_PRICE,
      valueGetter: (params: TypedValueGetterParams<FatFingerUserLimitDetail>) => params?.data?.defaultFatFinger?.fixedRatePrice,
      headerName: Locale.translate(Translation.TC_FIXED_RATE_PRICE),
      width: 150,
      filter: 'agNumberColumnFilter',
      valueFormatter: makeNumberFormatter({ minDp: 3, percent: true, decimalShift: 2 }),
      filterValueGetter: makeNumberFilterGetter({ minDp: 3, percent: true, decimalShift: 2 }),
      getQuickFilterText: makeNumberQuickFilterTextGetter({ percent: true })
    },
    {
      colId: TradingFatFingerGridColumnId.FAT_FINGER_FIXED_RATE_WARN_LEVEL,
      valueGetter: (params: TypedValueGetterParams<FatFingerUserLimitDetail>) => params?.data?.fatFingerUserLimit.fixedRateWarnLevel,
      headerName: Locale.translate(Translation.TC_FIXED_RATE_WARN_LEVEL),
      width: 180,
      filter: 'agNumberColumnFilter'
    },
    {
      colId: TradingFatFingerGridColumnId.FAT_FINGER_FIXED_RATE_RESTRICTION,
      valueGetter: (params: TypedValueGetterParams<FatFingerUserLimitDetail>) => params?.data?.fatFingerUserLimit.fixedRateRestriction,
      headerName: Locale.translate(Translation.TC_FIXED_RATE_RESTRICTION),
      width: 180,
      filter: 'agNumberColumnFilter'
    },
    {
      colId: TradingFatFingerGridColumnId.FAT_FINGER_FLOAT_RATE_SPREAD,
      valueGetter: (params: TypedValueGetterParams<FatFingerUserLimitDetail>) => params?.data?.defaultFatFinger?.floatRateSpread,
      headerName: Locale.translate(Translation.SC_FLOAT_RATE_SPREAD),
      width: 180,
      filter: 'agNumberColumnFilter',
      valueFormatter: makeNumberFormatter({ decimalShift: 4, separator: false }),
      filterValueGetter: makeNumberFilterGetter({ decimalShift: 4, separator: false })
    },
    {
      colId: TradingFatFingerGridColumnId.FAT_FINGER_FLOAT_RATE_WARN_LEVEL,
      valueGetter: (params: TypedValueGetterParams<FatFingerUserLimitDetail>) => params?.data?.fatFingerUserLimit.floatRateWarnLevel,
      headerName: Locale.translate(Translation.TC_FLOAT_RATE_WARN_LEVEL),
      width: 180,
      filter: 'agNumberColumnFilter'
    },
    {
      colId: TradingFatFingerGridColumnId.FAT_FINGER_FLOAT_RATE_RESTRICTION,
      valueGetter: (params: TypedValueGetterParams<FatFingerUserLimitDetail>) => params?.data?.fatFingerUserLimit.floatRateRestriction,
      headerName: Locale.translate(Translation.TC_FLOAT_RATE_RESTRICTION),
      width: 180,
      filter: 'agNumberColumnFilter'
    },
    {
      colId: TradingFatFingerGridColumnId.FAT_FINGER_NOTIONAL_AMOUNT_WARN,
      valueGetter: (params: TypedValueGetterParams<FatFingerUserLimitDetail>) => params?.data?.fatFingerUserLimit.notionalAmountWarn,
      headerName: Locale.translate(Translation.TC_NOTIONAL_AMOUNT_WARN),
      width: 200,
      filter: 'agNumberColumnFilter',
      valueFormatter: makeNumberFormatter({ decimalShift: -6, separator: true }),
      filterValueGetter: makeNumberFilterGetter({ decimalShift: -6, separator: true })
    },
    {
      colId: TradingFatFingerGridColumnId.FAT_FINGER_NOTIONAL_AMOUNT_RESTRICTION,
      valueGetter: (params: TypedValueGetterParams<FatFingerUserLimitDetail>) => params?.data?.fatFingerUserLimit.notionalAmountRestriction,
      headerName: Locale.translate(Translation.TC_NOTIONAL_AMOUNT_RESTRICTION),
      width: 210,
      filter: 'agNumberColumnFilter',
      valueFormatter: makeNumberFormatter({ decimalShift: -6, separator: true }),
      filterValueGetter: makeNumberFilterGetter({ decimalShift: -6, separator: true })
    },
    {
      colId: TradingFatFingerGridColumnId.FAT_FINGER_CASH_AMOUNT_WARN,
      valueGetter: (params: TypedValueGetterParams<FatFingerUserLimitDetail>) => params?.data?.fatFingerUserLimit.cashAmountWarn,
      headerName: Locale.translate(Translation.TC_CASH_AMOUNT_WARN),
      width: 180,
      filter: 'agNumberColumnFilter',
      valueFormatter: makeNumberFormatter({ decimalShift: -6, separator: true }),
      filterValueGetter: makeNumberFilterGetter({ decimalShift: -6, separator: true })
    },
    {
      colId: TradingFatFingerGridColumnId.FAT_FINGER_CASH_AMOUNT_RESTRICTION,
      valueGetter: (params: TypedValueGetterParams<FatFingerUserLimitDetail>) => params?.data?.fatFingerUserLimit.cashAmountRestriction,
      headerName: Locale.translate(Translation.TC_CASH_AMOUNT_RESTRICTION),
      width: 200,
      filter: 'agNumberColumnFilter',
      valueFormatter: makeNumberFormatter({ decimalShift: -6, separator: true }),
      filterValueGetter: makeNumberFilterGetter({ decimalShift: -6, separator: true })
    }
  ]
}

export const fatFingerLimitsGridOptions: GridOptions = {
  ...gridOptionsBase,
  groupDefaultExpanded: 0
}
